import { useCallback, useMemo } from "react"

import { copyContent } from "@/lib/copyContent"
import { applySharingRules } from "@/lib/applySharingRules"
import { applyPublishingRules } from "@/lib/applyPublishingRules"
import { type LicenseLogo } from "@/lib/getLicenseLogos"
import { EMBED_SCRIPT_URL, generateEmbedSnippet } from "@/lib/generateEmbedSnippet"
import { buildWidgetLogoPng, buildWidgetLogoSvg, DEFAULT_WIDGET_PNG_DPI } from "@/lib/widgetLogoImage"
import type { LicenseData } from "./useLicenseStore"

export type AssetKey = "markdown" | "json" | "html"
export type DownloadFormat = AssetKey | "zip" | "pdf" | "widget-png" | "widget-svg"

type Asset = {
  name: string
  type: string
  content: string
}

type AssetMap = Record<AssetKey, Asset>

export type AssetOption = {
  key: DownloadFormat
  label: string
}

const DOWNLOAD_OPTIONS: AssetOption[] = [
  { key: "zip", label: "Everything (.zip)" },
  { key: "pdf", label: "License (.pdf)" },
  { key: "markdown", label: "License (.md)" },
  { key: "json", label: "License (.json)" },
  { key: "html", label: "Widget (.html)" },
  { key: "widget-png", label: "Widget (.png)" },
  { key: "widget-svg", label: "Widget (.svg)" }
]

const COPY_OPTIONS: AssetOption[] = [
  { key: "markdown", label: "License Markdown" },
  { key: "json", label: "License JSON" },
  { key: "html", label: "Widget HTML" }
]

const WIDGET_DOWNLOAD_OPTIONS: AssetOption[] = [
  { key: "widget-png", label: "Widget (.png)" },
  { key: "widget-svg", label: "Widget (.svg)" }
]

interface UseLicenseAssetsOptions {
  markdown: string
  exportMarkdown?: string
  rawData: LicenseData
  logos: LicenseLogo[]
  licenseName: string
  licenseCode?: string
  embedTheme: "light" | "dark" | "auto"
  embedSize: "sm" | "md" | "lg"
}

const buildEmbedDocument = (snippet: string) => `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>ADBEx Data Sharing License Widget</title>
  </head>
  <body>
    ${snippet}
  </body>
</html>`

export const useLicenseAssets = ({
  markdown,
  exportMarkdown,
  rawData,
  logos,
  licenseName,
  licenseCode,
  embedTheme,
  embedSize
}: UseLicenseAssetsOptions) => {
  const markdownForExport = exportMarkdown ?? markdown
  const embedHtml = useMemo(
    () =>
      generateEmbedSnippet({
        logos,
        licenseName,
        licenseCode,
        theme: embedTheme,
        size: embedSize
      }),
    [embedSize, embedTheme, licenseCode, licenseName, logos]
  )
  const isEmbedReady = logos.length > 0

  const json = useMemo(
    () => JSON.stringify(applyPublishingRules(applySharingRules(rawData)), null, 2),
    [rawData]
  )

  const embedDocument = useMemo(() => buildEmbedDocument(embedHtml), [embedHtml])
  const embedDocumentLocal = useMemo(
    () => buildEmbedDocument(embedHtml.replace(EMBED_SCRIPT_URL, "./license-widget.min.js")),
    [embedHtml]
  )
  const widgetImageDpi = DEFAULT_WIDGET_PNG_DPI

  const assets = useMemo<AssetMap>(
    () => ({
      markdown: {
        name: "license.md",
        type: "text/markdown",
        content: markdownForExport
      },
      json: {
        name: "license.json",
        type: "application/json",
        content: json
      },
      html: {
        name: "license-widget.html",
        type: "text/html",
        content: embedDocument
      }
    }),
    [embedDocument, json, markdownForExport]
  )

  const copyAssets = useMemo<AssetMap>(
    () => ({
      markdown: {
        name: "license.md",
        type: "text/markdown",
        content: markdownForExport
      },
      json: {
        name: "license.json",
        type: "application/json",
        content: json
      },
      html: {
        name: "license-widget.html",
        type: "text/html",
        content: embedHtml
      }
    }),
    [embedHtml, json, markdownForExport]
  )

  const triggerDownload = useCallback((blob: Blob, name: string) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = name
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }, [])

  const createWidgetSvg = useCallback(async () => {
    if (!logos.length) return null
    return buildWidgetLogoSvg({ logos, licenseName })
  }, [licenseName, logos])

  const createWidgetPng = useCallback(
    async (svgResult?: Awaited<ReturnType<typeof buildWidgetLogoSvg>> | null) => {
      const resolved = svgResult ?? (await createWidgetSvg())
      if (!resolved) return null
      return buildWidgetLogoPng(resolved, { dpi: widgetImageDpi })
    },
    [createWidgetSvg, widgetImageDpi]
  )

  const createPdf = useCallback(async () => {
    const { PDFDocument, StandardFonts, rgb, PDFName, PDFString } = await import("pdf-lib")
    const doc = await PDFDocument.create()
    const fontRegular = await doc.embedFont(StandardFonts.Helvetica)
    const fontBold = await doc.embedFont(StandardFonts.HelveticaBold)
    const fontItalic = await doc.embedFont(StandardFonts.HelveticaOblique)
    const color = rgb(0.12, 0.16, 0.24)
    const margin = 54
    const footerHeight = 40
    const headerGap = 24
    const h2Gap = 18
    const bodyTextSize = 11
    const maxWidth = () => page.getWidth() - margin * 2
    let page = doc.addPage()
    let y = page.getHeight() - margin

    const normalizeText = (input: string) =>
      input
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/__(.*?)__/g, "$1")
        .replace(/`([^`]+)`/g, "$1")
        .replace(/\[(.*?)\]\((.*?)\)/g, "$1 ($2)")
        .replace(/_(.*?)_/g, "$1")
        .replace(/\*(.*?)\*/g, "$1")
        .trim()

    const lines = markdownForExport.replace(/\r\n/g, "\n").split("\n")
    let headerTitle = ""
    let headerMeta = ""
    let contentLines = lines
    const firstIndex = lines.findIndex(line => line.trim() !== "")
    if (firstIndex !== -1 && lines[firstIndex].trim().startsWith("# ")) {
      headerTitle = normalizeText(lines[firstIndex].trim().replace(/^#\s+/, ""))
      let nextIndex = firstIndex + 1
      while (nextIndex < lines.length && !lines[nextIndex].trim()) nextIndex += 1
      if (nextIndex < lines.length && /^_.*_$/.test(lines[nextIndex].trim())) {
        headerMeta = normalizeText(lines[nextIndex].trim().replace(/^_+|_+$/g, ""))
        contentLines = lines.filter((_, index) => index < firstIndex || index > nextIndex)
      } else {
        contentLines = lines.filter((_, index) => index < firstIndex || index > firstIndex)
      }
    }

    let logoImage: Awaited<ReturnType<typeof doc.embedPng>> | null = null
    const response = await fetch("/logomark.png")
    if (response.ok) {
      const logoBytes = await response.arrayBuffer()
      logoImage = await doc.embedPng(logoBytes)
    }

    const wrapText = (input: string, font: typeof fontRegular, size: number, width: number) => {
      if (!input.trim()) return [""]
      const words = input.split(/\s+/)
      const lines: string[] = []
      let current = ""

      words.forEach(word => {
        const next = current ? `${current} ${word}` : word
        if (font.widthOfTextAtSize(next, size) <= width) {
          current = next
          return
        }
        if (current) lines.push(current)
        current = word
      })

      if (current) lines.push(current)
      return lines
    }

    const renderHeader = () => {
      let headerY = page.getHeight() - margin
      if (headerTitle) {
        page.drawText(headerTitle, {
          x: margin,
          y: headerY,
          size: 14,
          font: fontBold,
          color
        })
        headerY -= 16
      }
      if (headerMeta) {
        page.drawText(headerMeta, {
          x: margin,
          y: headerY,
          size: 9,
          font: fontItalic,
          color
        })
        headerY -= 12
      }
      if (headerTitle || headerMeta) {
        const lineY = headerY - 5
        page.drawLine({
          start: { x: margin, y: lineY },
          end: { x: page.getWidth() - margin, y: lineY },
          thickness: 0.5,
          color: rgb(0.78, 0.8, 0.84)
        })
        return lineY - headerGap
      }
      return page.getHeight() - margin
    }

    let footerLimitY = margin + footerHeight
    const renderFooter = () => {
      const footerPrefix = "The Data Sharing License, by ADBEx. Created using"
      const footerUrl = "https://license-builder.adbex.org/"
      const textSize = 9
      const lineHeight = textSize * 1.3
      const logoSize = 14
      const logoGap = logoImage ? 8 : 0
      const lineCount = 1
      const bottomBaseline = margin + textSize
      const footerTop = bottomBaseline + (lineCount - 1) * lineHeight
      const textOffset = 18
      const logoOffset = 10
      const dividerGap = 4
      const contentGap = headerGap
      const lineY = footerTop + dividerGap
      footerLimitY = lineY + contentGap + textOffset

      page.drawLine({
        start: { x: margin, y: lineY },
        end: { x: page.getWidth() - margin, y: lineY },
        thickness: 0.5,
        color: rgb(0.78, 0.8, 0.84)
      })

      const textY = footerTop - textOffset
      const textX = margin + (logoImage ? logoSize + logoGap : 0)
      const prefixText = `${footerPrefix} `
      page.drawText(prefixText, {
        x: textX,
        y: textY,
        size: textSize,
        font: fontRegular,
        color
      })
      const prefixWidth = fontRegular.widthOfTextAtSize(prefixText, textSize)
      const linkX = textX + prefixWidth
      page.drawText(footerUrl, {
        x: linkX,
        y: textY,
        size: textSize,
        font: fontRegular,
        color
      })
      const linkWidth = fontRegular.widthOfTextAtSize(footerUrl, textSize)
      page.drawLine({
        start: { x: linkX, y: textY - 1 },
        end: { x: linkX + linkWidth, y: textY - 1 },
        thickness: 0.5,
        color
      })
      const linkAnnotation = doc.context.register(
        doc.context.obj({
          Type: PDFName.of("Annot"),
          Subtype: PDFName.of("Link"),
          Rect: [linkX, textY - 2, linkX + linkWidth, textY + textSize + 2],
          Border: [0, 0, 0],
          A: {
            Type: PDFName.of("Action"),
            S: PDFName.of("URI"),
            URI: PDFString.of(footerUrl)
          }
        })
      )
      page.node.addAnnot(linkAnnotation)

      if (logoImage) {
        const logoY = footerTop - logoSize + 2 - logoOffset
        page.drawImage(logoImage, {
          x: margin,
          y: logoY,
          width: logoSize,
          height: logoSize
        })
      }
    }

    y = renderHeader()
    renderFooter()

    const ensureSpace = (height: number) => {
      if (y - height < footerLimitY) {
        page = doc.addPage()
        y = renderHeader()
        renderFooter()
      }
    }

    const drawLines = (lines: string[], font: typeof fontRegular, size: number, indent = 0) => {
      lines.forEach(line => {
        ensureSpace(size * 1.4)
        page.drawText(line, {
          x: margin + indent,
          y,
          size,
          font,
          color
        })
        y -= size * 1.4
      })
    }

    let seenH2 = false
    contentLines.forEach(rawLine => {
      if (!rawLine.trim()) {
        y -= 8
        return
      }
      const line = rawLine.trim()
      if (line.startsWith("# ")) {
        const text = normalizeText(line.replace(/^#\s+/, ""))
        drawLines(wrapText(text, fontBold, 18, maxWidth()), fontBold, 18)
        y -= 4
        return
      }
      if (line.startsWith("## ")) {
        const text = normalizeText(line.replace(/^##\s+/, ""))
        if (seenH2) {
          ensureSpace(h2Gap)
          y -= h2Gap
        }
        seenH2 = true
        drawLines(wrapText(text, fontBold, 13, maxWidth()), fontBold, 13)
        y -= 2
        return
      }
      if (/^_.*_$/.test(line)) {
        const text = normalizeText(line.replace(/^_+|_+$/g, ""))
        drawLines(wrapText(text, fontItalic, 10, maxWidth()), fontItalic, 10)
        y -= 2
        return
      }

      const isRoman = /^\s*[ivxlcdm]+\.\s+/i.test(rawLine) && /^\s+/.test(rawLine)
      const isNumbered = /^\d+\.\s+/.test(line)
      if (isRoman || isNumbered) {
        const indent = isRoman ? 16 : 0
        const text = normalizeText(line)
        drawLines(wrapText(text, fontRegular, bodyTextSize, maxWidth() - indent), fontRegular, bodyTextSize, indent)
        return
      }

      const text = normalizeText(line)
      drawLines(wrapText(text, fontRegular, bodyTextSize, maxWidth()), fontRegular, bodyTextSize)
    })

    return doc.save()
  }, [markdown])

  const download = useCallback(
    async (format: DownloadFormat) => {
      if (format === "zip") {
        const { default: JSZip } = await import("jszip")
        const zip = new JSZip()
        Object.entries(assets).forEach(([key, asset]) => {
          if (key === "html") return
          zip.file(asset.name, asset.content)
        })
        const pdfContent = await createPdf()
        zip.file("license.pdf", pdfContent)
        let embedHtmlForZip = embedDocument
        try {
          const response = await fetch("/embed/license-widget.min.js")
          if (response.ok) {
            const script = await response.text()
            zip.file("license-widget.min.js", script)
            embedHtmlForZip = embedDocumentLocal
          }
        } catch {
          // Ignore embed script failures; remote embed will still work.
        }
        zip.file("license-widget.html", embedHtmlForZip)
        const widgetSvg = await createWidgetSvg()
        if (widgetSvg) {
          zip.file("license-widget.svg", widgetSvg.svg)
          const widgetPng = await createWidgetPng(widgetSvg)
          if (widgetPng) {
            zip.file("license-widget.png", widgetPng)
          }
        }
        const blob = await zip.generateAsync({ type: "blob" })
        triggerDownload(blob, "license-bundle.zip")
        return
      }

      if (format === "pdf") {
        const pdfContent = await createPdf()
        triggerDownload(
          new Blob([pdfContent], { type: "application/pdf" }),
          "license.pdf"
        )
        return
      }

      if (format === "widget-svg") {
        const widgetSvg = await createWidgetSvg()
        if (!widgetSvg) return
        triggerDownload(
          new Blob([widgetSvg.svg], { type: "image/svg+xml" }),
          "license-widget.svg"
        )
        return
      }

      if (format === "widget-png") {
        const widgetSvg = await createWidgetSvg()
        if (!widgetSvg) return
        const widgetPng = await createWidgetPng(widgetSvg)
        if (!widgetPng) return
        triggerDownload(widgetPng, "license-widget.png")
        return
      }

      const asset = assets[format]
      if (!asset) return

      triggerDownload(
        new Blob([asset.content], { type: asset.type }),
        asset.name
      )
    },
    [assets, createPdf, createWidgetPng, createWidgetSvg, embedDocument, embedDocumentLocal, triggerDownload]
  )

  const copyAsset = useCallback(
    async (format: DownloadFormat) => {
      if (format === "zip" || format === "pdf") return
      const asset = copyAssets[format]
      if (!asset) return
      await copyContent(asset.content)
    },
    [copyAssets]
  )

  return {
    json,
    embedHtml,
    isEmbedReady,
    download,
    copyAsset,
    downloadOptions: DOWNLOAD_OPTIONS,
    copyOptions: COPY_OPTIONS,
    widgetDownloadOptions: WIDGET_DOWNLOAD_OPTIONS
  }
}
